import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart2, ChevronRight, Zap, Target, BookOpen, CheckCircle2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ReactGA from 'react-ga4';
import { setIsLoginDialog } from '../../redux/reducers/loginSlice';

const productsData = [
    {
        id: 'assessments',
        title: 'Smart Assessments',
        category: 'For Academies & Centers',
        icon: Target,
        image: '/assets/generated/smart_assessments.png',
        description: 'Standardized, AI-assisted skill assessments from simple training videos.',
        details: 'Benchmark technique, footwork, and consistency across batches and age groups. Deliver clear, visual reports to players and parents after every assessment cycle.',
        outcomes: [
            'Benchmark technique & consistency',
            'Visual reports for players & parents'
        ],
        color: 'text-emerald',
        darkBg: 'bg-emerald/10',
        lightBg: 'bg-emerald/10',
        darkBorder: 'border-emerald/20',
        lightBorder: 'border-emerald/20',
        gradient: 'from-emerald/20 to-emerald/5',
        lightGradient: 'from-emerald-50 to-white'
    },
    {
        id: 'insights',
        title: 'Smart Insights',
        category: 'For Coaches & Analysts',
        icon: BarChart2,
        image: '/assets/generated/smart_insights.png',
        description: 'Automated match understanding—rallies, shot types, error patterns, and tactical moments.',
        details: 'See patterns in shot selection, weak zones, and scoring phases across matches. Use visual clips in video review sessions instead of manual note-taking.',
        outcomes: [
            'Identify weak zones & scoring phases',
            'Automated video clipping'
        ],
        color: 'text-blue-500',
        darkBg: 'bg-blue-500/10',
        lightBg: 'bg-blue-50',
        darkBorder: 'border-blue-500/20',
        lightBorder: 'border-blue-200',
        gradient: 'from-blue-500/20 to-blue-900/10',
        lightGradient: 'from-blue-50 to-white'
    },
    {
        id: 'journals',
        title: 'Smart Journals',
        category: 'For Athletes',
        icon: BookOpen,
        image: '/assets/generated/smart_journals.png',
        description: 'A personal video logbook where athletes track assessments, matches, and training.',
        details: 'Capture every session in one place, automatically tagged by skill, drill, or match situation. Spot trends in performance before they show up on the scoreboard.',
        outcomes: [
            'All sessions in one place',
            'Spot performance trends early'
        ],
        color: 'text-purple-500',
        darkBg: 'bg-purple-500/10',
        lightBg: 'bg-purple-50',
        darkBorder: 'border-purple-500/20',
        lightBorder: 'border-purple-200',
        gradient: 'from-purple-500/20 to-purple-900/10',
        lightGradient: 'from-purple-50 to-white'
    }
];

const ProductSection = ({ product, index, navigate }) => {
    const isEven = index % 2 === 0; // Even indices (0, 2) are Black. Odd (1, 3) are White.
    const isDark = isEven;

    // Theme Classes
    const theme = {
        bg: isDark ? 'bg-[#050505]' : 'bg-white',
        textPrimary: isDark ? 'text-white' : 'text-gray-900',
        textSecondary: isDark ? 'text-gray-400' : 'text-gray-600',
        textDetails: isDark ? 'text-gray-300' : 'text-gray-700',
        cardBg: isDark ? 'bg-[#0a0a0a]' : 'bg-white',
        cardBorder: isDark ? 'border-white/10 group-hover:border-white/20' : 'border-gray-200 group-hover:border-gray-300',
        badgeBg: isDark ? product.darkBg : product.lightBg,
        badgeBorder: isDark ? product.darkBorder : product.lightBorder,
        glowOpacity: isDark ? 'opacity-20' : 'opacity-40', // Reduced for performance
        glowBlend: 'mix-blend-normal', // Removed heavy blend modes
        techOverlay: isDark ? 'border-white/5' : 'border-black/5',
        cornerBorder: isDark ? 'border-white/10' : 'border-black/10',
        glassgradient: 'from-black/90 to-transparent',
        glassText: 'text-white/90',
        shadow: isDark ? 'shadow-lg' : 'shadow-md', // Reduced shadows
        listIconColor: isDark ? 'text-gray-200' : 'text-gray-800'
    };

    return (
        <div className={`py-20 first:pt-12 relative overflow-hidden ${theme.bg}`}>
            {/* Background Glow - Static and Simple */}
            <div className={`absolute top-1/2 ${isEven ? 'left-0' : 'right-0'} w-[400px] h-[400px] bg-gradient-to-r ${product.gradient} rounded-full blur-[60px] -translate-y-1/2 ${theme.glowOpacity} pointer-events-none transform-gpu`} />

            <div className="container mx-auto px-6 relative z-10">
                <div className={`flex flex-col lg:flex-row items-center gap-16 ${isEven ? '' : 'lg:flex-row-reverse'}`}>

                    {/* Content Side - Simple Fade In */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true, margin: "0px" }}
                        className="flex-1 space-y-6 transform-gpu"
                    >
                        {/* Header */}
                        <div>
                            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${theme.badgeBg} border ${theme.badgeBorder} mb-4`}>
                                <product.icon size={16} className={product.color} />
                                <span className={`text-xs font-bold uppercase tracking-wider ${product.color}`}>{product.category}</span>
                            </div>
                            <h2 className={`text-4xl lg:text-5xl font-bold ${theme.textPrimary} mb-4 tracking-tight`}>{product.title}</h2>
                            <p className={`text-xl ${theme.textSecondary} font-light leading-relaxed`}>{product.description}</p>
                        </div>

                        <p className={`${theme.textDetails} leading-relaxed text-lg font-normal`}>
                            {product.details}
                        </p>

                        {/* Outcomes Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {product.outcomes.map((outcome, idx) => (
                                <div key={idx} className="flex items-start gap-3">
                                    <CheckCircle2 size={20} className={`shrink-0 mt-1 ${product.color}`} />
                                    <span className={`${theme.listIconColor} font-medium`}>{outcome}</span>
                                </div>
                            ))}
                        </div>

                        <div className="pt-4">
                            <button
                                onClick={() => {
                                    if (product.id === 'assessments') {
                                        navigate('/products/smart-assessments');
                                    } else if (product.id === 'insights') {
                                        navigate('/products/smart-insights');
                                    } else if (product.id === 'journals') {
                                        navigate('/products/smart-journals');
                                    }
                                }}
                                className={`group inline-flex items-center gap-2 text-base font-semibold ${product.color} hover:opacity-80 transition-opacity cursor-pointer`}
                            >
                                Learn more
                                <ChevronRight size={18} className="transition-transform group-hover:translate-x-1" />
                            </button>
                        </div>
                    </motion.div>

                    {/* Image Side - Static Card, no hover scale if performance is key */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="flex-1 w-full transform-gpu"
                    >
                        <div className="relative group">
                            {/* Simple Glow behind card */}
                            <div className={`absolute -inset-1 rounded-[32px] bg-gradient-to-br ${product.gradient} opacity-20 blur-lg`} />

                            <div className={`relative rounded-3xl overflow-hidden ${theme.cardBg} border ${theme.cardBorder} ${theme.shadow}`}>
                                <img
                                    src={product.image}
                                    alt={product.title}
                                    className="w-full h-auto object-cover min-h-[300px] max-h-[500px]"
                                    loading="lazy"
                                />

                                {/* Tech Overlay lines */}
                                <div className={`absolute inset-0 border ${theme.techOverlay} pointer-events-none rounded-3xl`} />
                                <div className={`absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 ${theme.cornerBorder} rounded-tl-3xl`} />
                                <div className={`absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 ${theme.cornerBorder} rounded-br-3xl`} />

                                {/* Glass Overlay on Image Bottom */}
                                <div className={`absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t ${theme.glassgradient} flex items-end p-6`}>

                                    {/* Visual Intelligence Badge */}
                                    <div className={`flex items-center gap-2 ${theme.glassText}`}>
                                        <Zap size={16} className={product.color} fill="currentColor" />
                                        <span className={`text-sm font-mono font-bold tracking-wide ${product.color}`}>VISUAL INTELLIGENCE™</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                </div>
            </div>
        </div>
    );
}

const Products = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        window.scrollTo(0, 0);
        try {
            ReactGA.send({ hitType: 'pageview', page: location.pathname || '/products', title: 'Products' });
        } catch { }
    }, [location.pathname]);

    return (
        <div className="min-h-screen bg-[#050505] selection:bg-emerald/30 selection:text-emerald font-sans overflow-x-hidden">

            {/* Background Gradients - Optimized, No Noise, No Blend Mode */}
            <div className="fixed inset-0 z-0 pointer-events-none">
                <div className="absolute top-0 left-0 right-0 h-[400px] bg-gradient-to-b from-emerald/5 to-transparent" />
            </div>

            {/* Hero Section - Always Dark */}
            <section className="relative pt-32 pb-20 overflow-hidden bg-[#050505]">
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto transform-gpu"
                    >
                        {/* Removed Modular Platform Badge */}

                        <h1 className="text-5xl md:text-7xl font-black text-white mb-8 tracking-tight leading-[1.1] uppercase">
                            One Platform. <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald via-neon-lime to-emerald">
                                Endless Intelligence.
                            </span>
                        </h1>

                        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed font-light max-w-3xl mx-auto">
                            Built to grow with you. Start with AI skill assessments, progress to match insights, and build a complete smart journal.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Products Stream with Alternating Backgrounds */}
            <div className="space-y-0 relative z-10">
                {productsData.map((product, index) => (
                    <ProductSection key={product.id} product={product} index={index} navigate={navigate} />
                ))}
            </div>

            {/* Bottom CTA - White Theme */}
            <section className="py-24 relative overflow-hidden bg-white">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-emerald/5 pointer-events-none" />

                <div className="container mx-auto px-6 text-center relative z-10">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-[3rem] p-12 md:p-16 shadow-xl relative overflow-hidden group transform-gpu"
                    >
                        {/* Hover Glow - Simplified */}
                        <div className="absolute top-0 right-0 w-80 h-80 bg-emerald/5 rounded-full blur-[60px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />

                        <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight">Ready to transform your game?</h2>
                        <p className="text-gray-600 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
                            Join thousands of athletes and coaches using VISIST to unlock their full potential with professional-grade insights.
                        </p>
                        <button
                            onClick={() => dispatch(setIsLoginDialog(true))}
                            className="px-6 py-3 md:px-8 md:py-4 bg-emerald hover:bg-emerald-600 text-white font-bold text-base md:text-lg rounded-full transition-all transform hover:scale-105 shadow-lg"
                        >
                            Sign In
                        </button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Products;
